import { MouseEvent, SyntheticEvent, useState } from "react";

import { css, CSSObject } from "@emotion/react";
import { ChipProps } from "@mui/material/Chip";

import { colors, DeleteCircleIcon } from "@dotnet/telematics-design-system";

import { svgFontSize } from "shared/styles/styleFunctions";

import { useStyleOptions } from "./hooks";
import Icon from "./Icon";
import { TAG_SIZES, TagProps } from "./types";

const Tag = ({
    size,
    color,
    title,
    isReadOnly,
    iconLeft,
    iconRight,
    iconLeftColor,
    iconRightColor,
    isNotSelectable,
    onClick,
    onDelete,
    ...props
}: TagProps & Omit<ChipProps, "size" | "color">) => {
    const { gray100, textDisabled } = colors;

    const [isSelected, setIsSelected] = useState(false);

    const styleVariants = useStyleOptions(isSelected);

    const handleClick = (e: MouseEvent<HTMLDivElement>) => {
        if (!isEnabled) return;

        e.stopPropagation();

        if (onClick) {
            !isNotSelectable && setIsSelected(!isSelected);
            onClick(e);
        }
    };

    const handleDelete = (e: SyntheticEvent) => {
        e.stopPropagation();
        onDelete?.(e);
    };

    const bgColorRest = styleVariants.colors[color].backgroundRest;
    const bgColorHover = styleVariants.colors[color].backgroundHover;
    const bgColorActive = styleVariants.colors[color].backgroundActive;
    const textColorRest = styleVariants.colors[color].textRest;
    const textColorActive = styleVariants.colors[color].textActive;
    const borderColor = styleVariants.colors[color].border;
    const borderColorHover = styleVariants.colors[color].borderHover;
    const borderColorActive = styleVariants.colors[color].borderActive;
    const iconColor = styleVariants.colors[color].icon;
    const iconColorHover = styleVariants.colors[color].iconHover;
    const iconColorActive = styleVariants.colors[color].iconActive;
    const padding = styleVariants.paddings[size];
    const fontSize = size === TAG_SIZES.Tiny ? "12px" : "14px";
    const lineHeight = size === TAG_SIZES.Tiny ? "16px" : "20px";
    const letterSpacing = size === TAG_SIZES.Tiny ? "0.4px" : "0.1px";

    const isDisabled = props.disabled;
    const isEnabled = !isDisabled && !isReadOnly;
    const isInteractive = Boolean(onClick) && isEnabled;
    const hasClearIcon = isEnabled && onDelete !== undefined;

    const renderRightIcon = () => {
        if (hasClearIcon) {
            return (
                <DeleteCircleIcon
                    onClick={handleDelete}
                    css={css({
                        ...svgFontSize(16),
                        color: iconColor,
                        cursor: "pointer",
                        transition:
                            "all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",

                        "&:hover": {
                            color: iconColorHover
                        },

                        "&:active": {
                            color: iconColorActive
                        }
                    })}
                />
            );
        }

        if (iconRight) {
            return (
                <Icon
                    icon={iconRight}
                    iconColor={iconColor}
                    iconColorCustom={iconRightColor}
                    isDisabled={isDisabled}
                />
            );
        }

        return undefined;
    };

    const getStateColors = (isHover: boolean): CSSObject => {
        if (!isInteractive) return {};

        if (isDisabled) {
            return { backgroundColor: gray100, borderColor: gray100 };
        }

        return isHover
            ? { backgroundColor: bgColorHover, borderColor: borderColorHover }
            : {
                  backgroundColor: bgColorActive,
                  borderColor: borderColorActive
              };
    };

    return (
        <div
            {...props}
            onClick={handleClick}
            css={css({
                display: "inline-flex",
                alignItems: "center",
                columnGap: "4px",
                border: `1px solid ${isDisabled ? gray100 : borderColor}`,
                borderRadius: "4px",
                cursor: isInteractive ? "pointer" : "auto",
                transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                backgroundColor: isDisabled ? gray100 : bgColorRest,
                wordBreak: "break-word",
                padding,

                "&:hover": getStateColors(true),
                "&:active": getStateColors(false)
            })}
        >
            {iconLeft && (
                <Icon
                    icon={iconLeft}
                    iconColor={iconColor}
                    iconColorCustom={iconLeftColor}
                    isDisabled={isDisabled}
                />
            )}

            <span
                css={css({
                    fontWeight: "500",
                    color: isDisabled ? textDisabled : textColorRest,
                    fontSize,
                    lineHeight,
                    letterSpacing,
                    flex: "1",

                    ...(!isDisabled && {
                        "&:active": {
                            color: textColorActive
                        }
                    })
                })}
            >
                {title}
            </span>

            {renderRightIcon()}
        </div>
    );
};

export { TAG_SIZES, TAG_COLORS } from "./types/enums";

export default Tag;
